/**
 * Grounded chat over the library, shown either as a full screen or as a panel
 * docked to the right of whatever screen is open.
 *
 * Every answer lists the documents it was retrieved from, so a claim can be
 * traced back to the extraction it came from. The backend decides what is
 * relevant; this screen only renders the exchange.
 *
 * The two placements are the same conversation in different chrome, not two
 * features: the turns live in `App`, so docking, expanding and closing never
 * interrupt a thread or lose an answer. Which one to use is the user's call -
 * asking about the grid you are looking at wants the panel, reading a long
 * comparison wants the screen.
 */
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

import type { ChatScope, ChatSource } from "../api";
import { Button, Icon, IconButton } from "../components/primitives";
import { Markdown } from "../markdown";

export interface Turn {
  role: "user" | "assistant";
  content: string;
  sources?: ChatSource[];
  /** What the answer was actually drawn from, as the server reported it. */
  scope?: ChatScope;
  /**
   * What the scope was called when the question was sent - "this drawing",
   * "2 selected documents". Recorded per turn rather than read from the current
   * selector, so scrolling back through a thread shows what each answer was
   * really about instead of relabelling every answer with the scope now set.
   */
  scopeLabel?: string;
}

/** Where the assistant is being shown. */
export type AssistantVariant = "screen" | "panel";

/** One thing the assistant can be pointed at. */
export interface ScopeOption {
  id: string;
  label: string;
  /** The documents it covers, for the line under the selector. */
  caption: string;
}

interface AssistantProps {
  turns: Turn[];
  draft: string;
  thinking: boolean;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onOpenDocument: (id: string) => void;
  /** How many documents the library holds, for the line above an answer's sources. */
  libraryCount: number;
  /** What the assistant is pointed at, and what else it could be pointed at. */
  scope: string;
  scopeOptions: ScopeOption[];
  onScopeChange: (id: string) => void;
  variant?: AssistantVariant;
  /** Leave the assistant screen. Omitted for the docked panel, which has a close. */
  onBack?: () => void;
  /** Header controls - dock, expand, close - which only the caller can decide. */
  actions?: ReactNode;
}

/**
 * The exchange itself: transcript and composer.
 *
 * Identical in both placements, so it is written once. Only its padding and
 * measure change, which is CSS's business rather than this component's.
 */
function Conversation({
  turns,
  draft,
  thinking,
  onDraftChange,
  onSend,
  onOpenDocument,
  libraryCount,
  variant,
  scope,
  scopeOptions,
  onScopeChange,
}: Required<
  Pick<
    AssistantProps,
    | "turns"
    | "draft"
    | "thinking"
    | "onDraftChange"
    | "onSend"
    | "onOpenDocument"
    | "libraryCount"
    | "scope"
    | "scopeOptions"
    | "onScopeChange"
  >
> & {
  variant: AssistantVariant;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const current = scopeOptions.find((option) => option.id === scope) ?? scopeOptions[0];

  useEffect(() => {
    const element = scrollRef.current;
    if (element) element.scrollTo(0, element.scrollHeight);
  }, [turns.length, thinking]);

  return (
    <div className={`chat ${variant === "panel" ? "chat--panel" : ""}`.trim()}>
      <div className="chat__scroll" ref={scrollRef}>
        <div className="chat__col">
          {turns.length === 0 && (
            <div className="chat__intro">
              {current?.id === "library"
                ? "Ask questions across your analyzed documents. The assistant retrieves the most relevant ones and reasons over the values that were extracted from them. If the library has nothing relevant it will say so rather than guess."
                : `Ask about ${current?.caption ?? "the selected documents"}. Nothing else is read, and no other document is mentioned - change that with the selector below.`}
            </div>
          )}

          {turns.map((turn, index) =>
            turn.role === "user" ? (
              <div className="bubble bubble--user" key={index}>
                {turn.content}
              </div>
            ) : (
              <div className="bubble bubble--ai" key={index}>
                <Markdown text={turn.content} className="bubble__text" />
                {/*
                  Sources keep the order the retrieval returned them in, which is
                  most relevant first. The similarity score itself is not shown: it
                  is a cosine distance, so it reads as a confidence the model never
                  expressed, and the ranking already carries the useful part.
                */}
                {turn.sources && turn.sources.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {/*
                      Said plainly, because the list is shorter than the library and
                      that looked like a contradiction: an answer reading "the
                      library holds 7 documents" sat directly above five file cards,
                      and the five read as the library disagreeing with the
                      sentence. They are the documents this answer was retrieved
                      from - the assistant searches the whole library and reads the
                      closest few - so the line says which of the two it is.
                    */}
                    <div className="source__lead">
                      {turn.scope === "pinned"
                        ? `Answered from ${turn.scopeLabel ?? "the pinned documents"} only`
                        : turn.sources.length < libraryCount
                          ? `Answered from ${turn.sources.length} of ${libraryCount} documents - the closest to the question`
                          : `Answered from ${turn.sources.length === 1 ? "this document" : `all ${turn.sources.length} documents`}`}
                    </div>
                    {/*
                      A pinned answer about one drawing lists nothing: the user
                      picked that drawing, is looking at it, and a file card
                      repeating its name under every reply was the clutter that
                      made a scoped conversation read like a search across the
                      library. Two or more pinned documents still list, because
                      then which one a claim came from is a real question.
                    */}
                    {!(turn.scope === "pinned" && turn.sources.length === 1) &&
                      turn.sources.map((source) => (
                        <div className="source" key={source.id}>
                          <Icon name="description" size={18} className="u-muted" />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <a
                              onClick={() => onOpenDocument(source.id)}
                              style={{ cursor: "pointer", fontSize: 12, fontWeight: 700, overflowWrap: "anywhere" }}
                            >
                              {source.name}
                            </a>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ),
          )}

          {thinking && (
            <div className="typing">
              <span />
              <span />
              <span />
            </div>
          )}
        </div>
      </div>

      <div className="chat__composer">
        <div className="chat__composer-col">
          {/*
            Directly above the box you type in, not in the panel header: what the
            next question will be answered from is part of asking it, and a
            caption anywhere else was read once and then forgotten. It names the
            documents rather than a mode - "this drawing: bracket.pdf" - because
            the confusion it settles is which documents, not which feature.
          */}
          <div className="chatscope">
            <Icon name="filter_alt" size={16} className="u-muted" />
            <span className="chatscope__label">Answering about</span>
            <select
              className="chatscope__select"
              value={current?.id ?? "library"}
              onChange={(event) => onScopeChange(event.target.value)}
              aria-label="What the assistant answers about"
            >
              {scopeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="chatscope__caption u-truncate">{current?.caption}</span>
          </div>
          <textarea
            className="composer-input"
            rows={variant === "panel" ? 2 : 3}
            placeholder="Ask about your documents..."
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                onSend();
              }
            }}
          />
          <Button
            variant="primary"
            size={variant === "panel" ? "md" : "lg"}
            icon="arrow_upward"
            onClick={onSend}
            disabled={thinking || !draft.trim()}
            style={{ alignSelf: "flex-start" }}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Assistant({
  turns,
  draft,
  thinking,
  onDraftChange,
  onSend,
  onOpenDocument,
  libraryCount,
  scope,
  scopeOptions,
  onScopeChange,
  variant = "screen",
  onBack,
  actions,
}: AssistantProps) {
  const conversation = (
    <Conversation
      turns={turns}
      draft={draft}
      thinking={thinking}
      onDraftChange={onDraftChange}
      onSend={onSend}
      onOpenDocument={onOpenDocument}
      libraryCount={libraryCount}
      scope={scope}
      scopeOptions={scopeOptions}
      onScopeChange={onScopeChange}
      variant={variant}
    />
  );

  if (variant === "panel") {
    // `dac-inspector` is the design system's own right-hand column, and
    // `app-panel-slot` places it in the shell grid. The panel is a sibling of the
    // main content rather than an overlay, so the screen beside it keeps its full
    // width minus the panel - nothing is covered and nothing is unreachable.
    return (
      <aside className="dac-inspector app-panel-slot assistant-panel" aria-label="Assistant">
        <div className="dac-panel__head">
          <Icon name="forum" size={18} />
          <span className="dac-panel__title">Assistant</span>
          {actions}
        </div>
        {conversation}
      </aside>
    );
  }

  return (
    <section className="screen">
      {/*
        The same header as Documents and Compare: a way back on the left, then
        the title at the screen's own size. It used to be a centred 860px block
        with its own geometry, which made the title sit lower and further right
        than on every other screen.
      */}
      <div className="screen__head">
        {onBack && <IconButton icon="arrow_back" label="Back to the library" onClick={onBack} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 className="screen__title" style={{ marginBottom: 4 }}>
            Assistant
          </h1>
          <div className="screen__caption">
            Answers are grounded in the documents named under the box below - nothing else is read.
          </div>
        </div>
        {actions}
      </div>
      {conversation}
    </section>
  );
}
